/**
 * =============================================================================
 * App administration i18n (SK + EN) — FE issues #24 and #25
 * =============================================================================
 *
 * The admin portal still has no global i18n framework; per the platform-wide
 * mandate every NEW user-facing text ships in Slovak and English. This module
 * follows the pattern established by `promotionI18n.ts` (issue #23) and reuses
 * its language resolution, so one stored preference drives both.
 *
 * Covered here:
 * - editing an existing app (issue #25), including repointing a pinned digest
 *   to the branch's floating tag,
 * - the "this app cannot be deleted" conflict (issue #24),
 * - session expiry messages (issue #24),
 * - the app vocabulary of BE issue #52, which keeps two things apart:
 *     APPROVAL WORKFLOW   schvaliť / zamietnuť  - approve / reject
 *                         ("may this version go out at all?")
 *     OPERATIONAL SWITCH  aktivovať / deaktivovať - activate / deactivate
 *                         ("is it switched on right now?" - the approval stays
 *                          valid, so switching back on is instant)
 */

import { getLang, type Lang } from './promotionI18n'

export type { Lang }
export { getLang }

interface BiText {
  sk: string
  en: string
}

const TEXTS = {
  // --- Edit app (issue #25) ------------------------------------------------
  editAction: { sk: 'Upraviť', en: 'Edit' },
  editTitle: { sk: 'Upraviť aplikáciu', en: 'Edit app' },
  editIntro: {
    sk: 'Upraviť sa dá názov, popis, kategória, odkaz na obraz, podporované typy zariadení a konfiguračné YAML-y. Aplikáciu netreba mazať a zakladať znova.',
    en: 'You can change the name, description, category, image reference, supported device types and the configuration YAMLs. There is no need to delete and recreate the app.',
  },
  fieldName: { sk: 'Názov', en: 'Name' },
  fieldDescription: { sk: 'Popis', en: 'Description' },
  fieldCategory: { sk: 'Kategória', en: 'Category' },
  fieldImage: { sk: 'Odkaz na obraz (image:tag alebo @sha256:…)', en: 'Image reference (image:tag or @sha256:…)' },
  fieldDeviceTypes: { sk: 'Typy zariadení', en: 'Device types' },
  fieldConfig: { sk: 'Konfigurácia app-config.yaml (voliteľné)', en: 'Configuration app-config.yaml (optional)' },
  fieldParams: { sk: 'Parametre app-parameters.yaml (voliteľné)', en: 'Parameters app-parameters.yaml (optional)' },
  configKeepHint: {
    sk: 'Ak súbor nevyberiete, uloží sa pôvodná konfigurácia bez zmeny.',
    en: 'Leave the file empty to keep the stored configuration unchanged.',
  },
  selectedFile: { sk: 'Vybraný súbor', en: 'Selected file' },
  invalidImage: {
    sk: 'Očakáva sa host/projekt/nazov:tag alebo host/projekt/nazov@sha256:<digest>',
    en: 'Expected host/project/name:tag or host/project/name@sha256:<digest>',
  },
  branchLabel: { sk: 'Vetva', en: 'Branch' },
  save: { sk: 'Uložiť zmeny', en: 'Save changes' },
  saving: { sk: 'Ukladám…', en: 'Saving…' },
  cancel: { sk: 'Zrušiť', en: 'Cancel' },
  saveFailed: { sk: 'Uloženie zlyhalo.', en: 'Saving failed.' },
  noChanges: { sk: 'Žiadne zmeny na uloženie.', en: 'Nothing to save.' },

  // --- Pinned digest ↔ floating tag (issue #25) ----------------------------
  pinnedTitle: { sk: 'Aplikácia je pripnutá na pevný digest', en: 'App is pinned to a fixed digest' },
  pinnedBody: {
    sk: 'Zariadenia dostávajú presne tento obraz, takže nové buildy sa k nim nikdy nedostanú. Prepnutím na plávajúcu značku vetvy začne aplikácia dostávať aktuálne buildy.',
    en: 'Devices receive exactly this image, so new builds never reach them. Switching to the branch’s floating tag makes the app follow current builds again.',
  },
  switchToTag: { sk: 'Prepnúť na značku', en: 'Switch to tag' },
  pinCurrentDigest: { sk: 'Pripnúť aktuálny digest', en: 'Pin the current digest' },
  pinCurrentDigestHint: {
    sk: 'Zmrazí aplikáciu na obraz, ktorý beží teraz — nové buildy ju už neovplyvnia.',
    en: 'Freezes the app on the image running right now — new builds no longer affect it.',
  },
  floatingTagsTitle: { sk: 'Plávajúce značky v Harbore', en: 'Floating tags in Harbor' },
  floatingTagsNone: {
    sk: 'Harbor nevrátil pre tento repozitár žiadne plávajúce značky — odkaz zadajte ručne.',
    en: 'Harbor returned no floating tags for this repository — enter the reference manually.',
  },
  tagPointsToCurrent: { sk: 'ukazuje na aktuálny obraz', en: 'points to the current image' },
  loadingTags: { sk: 'Načítavam značky…', en: 'Loading tags…' },

  // --- Re-approval warning (issue #25) -------------------------------------
  reapprovalTitle: { sk: 'Zmena obrazu vyžiada opätovné schválenie', en: 'Changing the image requires re-approval' },
  reapprovalBody: {
    sk: 'Po uložení prejde aplikácia do stavu „čaká na schválenie“ a dovtedy zmizne zariadeniam z trhoviska. Vývojové (dev) aplikácie sa schvália automaticky pri najbližšej kontrole digestu.',
    en: 'After saving, the app moves to "pending approval" and disappears from the device marketplace until it is approved. Development (dev) apps are approved automatically at the next digest check.',
  },
  reapprovalProdNote: {
    sk: 'Produkčné aplikácie musí schváliť administrátor ručne.',
    en: 'Production apps have to be approved manually by an administrator.',
  },
  savedPending: {
    sk: 'Zmeny sú uložené. Aplikácia čaká na schválenie.',
    en: 'Changes saved. The app is waiting for approval.',
  },
  savedOk: { sk: 'Zmeny sú uložené.', en: 'Changes saved.' },

  // --- Delete (issue #24) --------------------------------------------------
  deleteAction: { sk: 'Zmazať', en: 'Delete' },
  deleteTitle: { sk: 'Zmazať aplikáciu', en: 'Delete app' },
  deleteConfirm: {
    sk: 'Naozaj zmazať túto aplikáciu? Akcia sa nedá vrátiť späť.',
    en: 'Really delete this app? This action cannot be undone.',
  },
  deleting: { sk: 'Mažem…', en: 'Deleting…' },
  deleteBlockedTitle: {
    sk: 'Aplikáciu nemožno zmazať — je použitá',
    en: 'This app cannot be deleted — it is in use',
  },
  deleteBlockedWhy: {
    sk: 'Mazanie by odstránilo aj históriu terapie pacientov, preto je zablokované. Nič sa nezmenilo.',
    en: 'Deleting it would take patients’ therapy history with it, so it is blocked. Nothing was changed.',
  },
  usedInPlans: { sk: 'rehabilitačných plánov', en: 'rehabilitation plan items' },
  usedInRecords: { sk: 'záznamov pacientov', en: 'patient records' },
  usedInMetrics: { sk: 'meraní ruky', en: 'hand metrics' },
  deleteAlternativesTitle: { sk: 'Čo sa dá spraviť namiesto toho', en: 'What you can do instead' },
  deleteAltDemote: {
    sk: 'Vrátiť aplikáciu do dev — okamžite prestane chodiť na zariadenia v normálnom režime, história ostane.',
    en: 'Return the app to dev — it immediately stops reaching devices in normal mode, the history is kept.',
  },
  deleteAltDeactivate: {
    sk: 'Deaktivovať aplikáciu — zariadenia ju okamžite prestanú dostávať, dáta aj schválenie ostávajú zachované.',
    en: 'Deactivate the app — devices stop receiving it immediately, while the data and the approval are preserved.',
  },
  deleteAltRepoint: {
    sk: 'Ak ste chceli len zmeniť obraz, netreba mazať — použite Upraviť.',
    en: 'If you only wanted to change the image, there is no need to delete — use Edit.',
  },
  deleteFailed: { sk: 'Mazanie zlyhalo.', en: 'Delete failed.' },
  close: { sk: 'Zavrieť', en: 'Close' },

  // --- Operational switch: activate / deactivate (issue #52) ---------------
  // NOT a review verdict. The approval and approved_image_digest survive a
  // deactivation, which is exactly why activation is instant.
  deactivateAction: { sk: 'Deaktivovať aplikáciu', en: 'Deactivate app' },
  activateAction: { sk: 'Aktivovať aplikáciu', en: 'Activate app' },
  deactivateTitle: { sk: 'Deaktivovať aplikáciu', en: 'Deactivate app' },
  activateTitle: { sk: 'Aktivovať aplikáciu', en: 'Activate app' },
  deactivateWarnTitle: {
    sk: 'Aplikácia zmizne zo zariadení',
    en: 'The app will disappear from devices',
  },
  deactivateWarnBody: {
    sk: 'Zariadenia ju prestanú dostávať okamžite. Nie je to zamietnutie: schválenie ani schválený digest sa nemenia, takže spätná aktivácia je jedno kliknutie a nevyžaduje nové schvaľovanie.',
    en: 'Devices stop receiving it immediately. This is not a rejection: the approval and the approved digest stay untouched, so switching it back on is a single click and needs no new review.',
  },
  activateWarnTitle: {
    sk: 'Aplikácia sa vráti na zariadenia',
    en: 'The app will return to devices',
  },
  activateWarnBody: {
    sk: 'Aplikácia sa okamžite objaví na zariadeniach — schválenie nebolo nikdy zrušené. Platí to, ak je v stave „schválená“ a jej obraz sa odvtedy nezmenil.',
    en: 'The app reappears on devices immediately — its approval was never revoked. This applies as long as its status is "approved" and its image has not changed since.',
  },
  deactivateReasonLabel: { sk: 'Dôvod (voliteľný)', en: 'Reason (optional)' },
  deactivateReasonPlaceholder: {
    sk: 'Napr. dočasne vypnutá počas údržby…',
    en: 'E.g. temporarily off during maintenance…',
  },
  confirmDeactivate: { sk: 'Deaktivovať', en: 'Deactivate' },
  confirmActivate: { sk: 'Aktivovať', en: 'Activate' },
  deactivatedOk: {
    sk: 'Aplikácia je deaktivovaná — zariadenia ju už nedostávajú.',
    en: 'The app is deactivated — devices no longer receive it.',
  },
  activatedOk: {
    sk: 'Aplikácia je aktivovaná — zariadenia ju opäť dostávajú.',
    en: 'The app is activated — devices receive it again.',
  },
  deactivatedBadge: { sk: 'Deaktivovaná', en: 'Deactivated' },
  deactivatedBannerTitle: { sk: 'Aplikácia je deaktivovaná', en: 'This app is deactivated' },
  deactivatedBannerBody: {
    sk: 'Zariadenia ju nedostávajú. Schválenie ostáva platné, takže aktivácia je okamžitá a nevyžaduje nové schvaľovanie.',
    en: 'Devices do not receive it. The approval remains valid, so activation is instant and needs no new review.',
  },
  switchFailed: { sk: 'Zmena stavu zlyhala.', en: 'Changing the state failed.' },

  // --- Approval workflow wording (issue #52) --------------------------------
  approveAction: { sk: 'Schváliť', en: 'Approve' },
  rejectAction: { sk: 'Zamietnuť', en: 'Reject' },
  approveRejectedAction: {
    sk: 'Schváliť zamietnutú verziu',
    en: 'Approve rejected version',
  },
  approveRejectedTitle: {
    sk: 'Schvaľujete predtým zamietnutú verziu',
    en: 'You are approving a previously rejected version',
  },
  approveRejectedBody: {
    sk: 'Táto aplikácia bola zamietnutá. Schválením ju pustíte na zariadenia. História sa neprepisuje — pôvodné zamietnutie aj jeho dôvod ostávajú v časovej osi, len pribudne nový záznam o schválení.',
    en: 'This app was rejected. Approving it releases it to devices. The history is not rewritten — the original rejection and its reason stay in the timeline, a new approval entry is simply appended.',
  },
  rejectedBannerTitle: { sk: 'Dôvod zamietnutia', en: 'Rejection reason' },
  rejectedBannerHint: {
    sk: 'Zamietnutie je verdikt schvaľovacieho procesu. Ak ste chceli aplikáciu len dočasne vypnúť, použite „Deaktivovať aplikáciu“ — schválenie vtedy ostáva platné.',
    en: 'A rejection is a review verdict. If you only wanted to switch the app off for a while, use "Deactivate app" instead — that keeps the approval valid.',
  },

  // --- Approve / Reject dialogs (issue #52) ---------------------------------
  // These two dialogs ARE the review workflow, so their wording has to be as
  // unambiguous as the buttons that open them — in both languages.
  approveTitle: { sk: 'Schváliť aplikáciu', en: 'Approve app' },
  approveIntroTitle: { sk: 'Schválenie aplikácie', en: 'Approving the app' },
  approveIntroBody: {
    sk: 'Chystáte sa schváliť túto aplikáciu. Schválený digest sa pripne na obraz, ktorý je práve nasadený.',
    en: 'You are about to approve this app. The approved digest is pinned to the image that is currently deployed.',
  },
  approveEffectsTitle: { sk: 'Čo sa stane po schválení', en: 'What happens after approval' },
  approveEffectDoctor: { sk: 'Aplikácia sa zobrazí v portáli pre lekárov', en: 'The app becomes visible in the Doctor Portal' },
  approveEffectAssign: { sk: 'Lekári ju budú môcť priradiť pacientom', en: 'Doctors can assign it to patients' },
  approveEffectDevices: { sk: 'Aplikácia bude dostupná na zariadeniach RehaPiano', en: 'The app becomes available on RehaPiano devices' },
  approveEffectDeactivated: {
    sk: 'Pozor: aplikácia je deaktivovaná, takže na zariadenia pôjde až po aktivácii.',
    en: 'Note: the app is deactivated, so it only reaches devices after you activate it.',
  },
  approveCommentLabel: { sk: 'Komentár (voliteľný)', en: 'Comment (optional)' },
  approveCommentPlaceholder: { sk: 'Poznámka k tomuto schváleniu…', en: 'A note about this approval…' },
  approveSubmit: { sk: 'Schváliť', en: 'Approve' },
  approveSubmitting: { sk: 'Schvaľujem…', en: 'Approving…' },

  rejectTitle: { sk: 'Zamietnuť aplikáciu', en: 'Reject app' },
  rejectIntroTitle: { sk: 'Zamietnutie aplikácie', en: 'Rejecting the app' },
  rejectIntroBody: {
    sk: 'Zamietate túto verziu — na zariadenia sa nedostane. Vývojár dostane oznámenie, môže ju opraviť a požiadať o schválenie znova; zamietnutie viete kedykoľvek zmeniť na schválenie.',
    en: 'You are rejecting this version — it will not reach any device. The developer is notified and can fix it and ask for approval again; a rejection can be turned into an approval at any time.',
  },
  rejectNotDeactivate: {
    sk: 'Ak chcete aplikáciu len dočasne vypnúť a schválenie ponechať v platnosti, použite „Deaktivovať aplikáciu“.',
    en: 'If you only want to switch the app off for a while and keep the approval valid, use "Deactivate app" instead.',
  },
  rejectReasonLabel: { sk: 'Krátky dôvod zamietnutia', en: 'Short rejection reason' },
  rejectReasonPlaceholder: {
    sk: 'Napr. chýbajúce povinné parametre, nevalidná konfigurácia',
    en: 'E.g. missing required parameters, invalid configuration',
  },
  rejectReasonHint: { sk: 'Stručné zhrnutie (max 200 znakov).', en: 'A brief summary (max 200 characters).' },
  rejectDetailLabel: { sk: 'Podrobný popis', en: 'Detailed description' },
  rejectDetailPlaceholder: { sk: 'Sem napíšte podrobnú spätnú väzbu…', en: 'Provide detailed feedback here…' },
  rejectSubmit: { sk: 'Zamietnuť', en: 'Reject' },
  rejectSubmitting: { sk: 'Zamietam…', en: 'Rejecting…' },

  // --- App list: filters, badges, stats (issue #52) -------------------------
  filterActivationLabel: { sk: 'Prevádzkový stav', en: 'Operational state' },
  filterActivationAll: { sk: 'Všetky', en: 'All' },
  filterActivationActive: { sk: 'Aktívne', en: 'Active' },
  filterActivationInactive: { sk: 'Deaktivované', en: 'Deactivated' },
  statsDeactivated: { sk: 'Deaktivované', en: 'Deactivated' },
  statusDraft: { sk: 'Koncept', en: 'Draft' },
  statusPending: { sk: 'Čaká na schválenie', en: 'Pending approval' },
  statusApproved: { sk: 'Schválená', en: 'Approved' },
  statusRejected: { sk: 'Zamietnutá', en: 'Rejected' },
  statusDeprecated: { sk: 'Vyradená', en: 'Deprecated' },

  // --- Session (issue #24) -------------------------------------------------
  sessionExpiredTitle: { sk: 'Relácia vypršala', en: 'Your session has expired' },
  sessionExpiredBody: {
    sk: 'Z bezpečnostných dôvodov ste boli odhlásený. Prihláste sa prosím znova — rozpracované údaje vo formulároch sa neukladajú.',
    en: 'You were signed out for security reasons. Please sign in again — unsaved form data is not kept.',
  },
  sessionRestoreFailedBody: {
    sk: 'Prihlásenie sa nepodarilo obnoviť. Prihláste sa prosím znova.',
    en: 'The sign-in could not be restored. Please sign in again.',
  },
} satisfies Record<string, BiText>

export type AppAdminTextKey = keyof typeof TEXTS

/** Translate one of the app-administration strings into the active language. */
export function at(key: AppAdminTextKey, lang: Lang = getLang()): string {
  return TEXTS[key][lang]
}

/** Both language variants, for `title=` tooltips (SK / EN). */
export function atBoth(key: AppAdminTextKey): string {
  return `${TEXTS[key].sk} / ${TEXTS[key].en}`
}
